import React, { useState, useEffect, useContext } from 'react';
import api from '../../../api/axiosInstance';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { AppContext } from '../../../context/AppContext';
import '../../../assets/css/admin/blogmanagement.css';

const CATEGORIES = [
  'Mental Health', 'Speech Therapy', 'Child Development',
  'Autism & Special Needs', 'Parenting Tips', 'Therapy Techniques',
  'Wellness & Lifestyle', 'Research & Studies'
];

const BlogManagement = () => {
  const { baseUrl } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'create'
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    id: null, title: '', handle: '', category: '', author: '',
    readTime: '', excerpt: '', content: '',
    metaTitle: '', metaDescription: '', status: 'draft',
    featured: false, allowComments: true,
  });
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState('basic');
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ===== FETCH DATA =====
  useEffect(() => {
    fetchBlogs();
    fetchTagSuggestions();
  }, []);

  const fetchBlogs = () => {
    setLoading(true);
    api.get(`${baseUrl}/admin/blogs`)
      .then((res) => setBlogs(res.data))
      .catch((err) => {
        console.error('Blog fetch error:', err);
      })
      .finally(() => setLoading(false));
  };

  const fetchTagSuggestions = () => {
    api.get(`${baseUrl}/admin/blogs/tags/suggestions`)
      .then((res) => setTagSuggestions(res.data))
      .catch((err) => console.error('Tag suggestions error:', err));
  };

  // ===== FORM HANDLERS =====
  const handleTitleChange = (e) => {
    const title = e.target.value;
    const handle = title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 60);
    setFormData({ ...formData, title, handle });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleThumbnail = (e) => {
    const file = e.target.files[0];
    if (file) { setThumbnail(file); setThumbnailPreview(URL.createObjectURL(file)); }
  };

  const addTag = (tag) => {
    const cleaned = tag.trim().toLowerCase();
    if (cleaned && !tags.includes(cleaned) && tags.length < 10) setTags([...tags, cleaned]);
    setTagInput('');
  };

  const removeTag = (tag) => setTags(tags.filter(t => t !== tag));

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); }
  };

  const resetForm = () => {
    setFormData({ id: null, title: '', handle: '', category: '', author: '', readTime: '', excerpt: '', content: '', metaTitle: '', metaDescription: '', status: 'draft', featured: false, allowComments: true });
    setTags([]); setTagInput(''); setThumbnail(null); setThumbnailPreview(null); setExistingThumbnailUrl(null);
    setErrors({}); setActiveSection('basic'); setIsEditing(false);
  };

  const validate = () => {
    const errs = {};
    if (!formData.title.trim())   errs.title    = 'Blog title is required';
    if (!formData.category)       errs.category = 'Please select a category';
    if (!formData.author.trim())  errs.author   = 'Author name is required';
    if (!formData.excerpt.trim()) errs.excerpt  = 'Short description is required';
    if (!formData.content.trim()) errs.content  = 'Blog content is required';
    return errs;
  };

  // ===== CREATE / UPDATE (backed by Laravel) =====
  const handleSubmit = (status) => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs); setActiveSection('basic');
      Swal.fire({ title: 'Incomplete Form', text: 'Please fill all required fields.', icon: 'warning', confirmButtonColor: '#ff7a00' });
      return;
    }
    setErrors({});
    setSubmitting(true);

    const fd = new FormData();
    fd.append('title', formData.title);
    fd.append('handle', formData.handle);
    fd.append('category', formData.category);
    fd.append('author', formData.author);
    fd.append('read_time', formData.readTime || '');
    fd.append('excerpt', formData.excerpt);
    fd.append('content', formData.content);
    fd.append('meta_title', formData.metaTitle || '');
    fd.append('meta_description', formData.metaDescription || '');
    fd.append('status', status);
    fd.append('featured', formData.featured ? 1 : 0);
    fd.append('allow_comments', formData.allowComments ? 1 : 0);
    tags.forEach(t => fd.append('tags[]', t));
    if (thumbnail) fd.append('thumbnail', thumbnail);

    const request = isEditing
      ? api.post(`${baseUrl}/admin/blogs/${formData.id}?_method=PUT`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      : api.post(`${baseUrl}/admin/blogs`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

    request
      .then((res) => {
        if (isEditing) {
          setBlogs(blogs.map(b => (b.id === res.data.id ? res.data : b)));
        } else {
          setBlogs([res.data, ...blogs]);
        }
        Swal.fire({
          title: status === 'published' ? 'Blog Published!' : 'Draft Saved!',
          html: `<strong>"${formData.title}"</strong> has been ${status === 'published' ? 'published.' : 'saved as draft.'}`,
          icon: 'success', confirmButtonColor: '#1a237e', timer: 2500
        });
        resetForm();
        setActiveTab('list');
      })
      .catch((err) => {
        console.error('Blog save error:', err);
        const serverErrors = err.response?.data?.errors;
        if (serverErrors) {
          const mapped = {};
          Object.keys(serverErrors).forEach(k => { mapped[k] = serverErrors[k][0]; });
          setErrors(mapped);
        }
        Swal.fire({ title: 'Error', text: 'Could not save the blog. Please check the form and try again.', icon: 'error', confirmButtonColor: '#ff7a00' });
      })
      .finally(() => setSubmitting(false));
  };

  // ===== EDIT =====
  const handleEdit = (blog) => {
    setFormData({
      id: blog.id,
      title: blog.title || '',
      handle: blog.handle || '',
      category: blog.category || '',
      author: blog.author || '',
      readTime: blog.read_time || '',
      excerpt: blog.excerpt || '',
      content: blog.content || '',
      metaTitle: blog.meta_title || '',
      metaDescription: blog.meta_description || '',
      status: blog.status || 'draft',
      featured: !!blog.featured,
      allowComments: blog.allow_comments !== undefined ? !!blog.allow_comments : true,
    });
    setTags((blog.tags || []).map(t => (typeof t === 'string' ? t : t.name)));
    setThumbnail(null);
    setThumbnailPreview(null);
    setExistingThumbnailUrl(blog.thumbnail ? `${baseUrl.replace('/api', '')}/storage/${blog.thumbnail}` : null);
    setErrors({});
    setActiveSection('basic');
    setIsEditing(true);
    setActiveTab('create');
  };

  // ===== DELETE =====
  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Blog?', text: 'This action cannot be undone.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#dc2626', cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel'
    }).then(result => {
      if (result.isConfirmed) {
        api
          .delete(`${baseUrl}/admin/blogs/${id}`)
          .then(() => {
            setBlogs(blogs.filter(b => b.id !== id));
            Swal.fire({ title: 'Deleted!', text: 'Blog has been deleted.', icon: 'success', confirmButtonColor: '#ff7a00', timer: 1800 });
          })
          .catch((err) => {
            console.error('Blog delete error:', err);
            Swal.fire({ title: 'Error', text: 'Could not delete the blog.', icon: 'error', confirmButtonColor: '#ff7a00' });
          });
      }
    });
  };

  // ===== STATUS TOGGLE =====
  const handleStatusToggle = (id) => {
    api
      .patch(`${baseUrl}/admin/blogs/${id}/toggle-status`)
      .then((res) => {
        setBlogs(blogs.map(b => (b.id === id ? res.data : b)));
      })
      .catch((err) => {
        console.error('Status toggle error:', err);
        Swal.fire({ title: 'Error', text: 'Could not update status.', icon: 'error', confirmButtonColor: '#ff7a00' });
      });
  };

  const filteredBlogs = blogs.filter(b => {
    const matchSearch = b.title.toLowerCase().includes(searchQuery.toLowerCase()) || b.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = filterStatus === 'all' || b.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const publishedCount = blogs.filter(b => b.status === 'published').length;
  const draftCount = blogs.filter(b => b.status === 'draft').length;
  const totalViews = blogs.reduce((sum, b) => sum + (b.views || 0), 0);

  const sections = [
    { id: 'basic',    label: 'Basic Info', icon: '📝' },
    { id: 'content',  label: 'Content',    icon: '✍️' },
    { id: 'media',    label: 'Media',      icon: '🖼️' },
    { id: 'seo',      label: 'SEO',        icon: '🔍' },
    { id: 'settings', label: 'Settings',   icon: '⚙️' },
  ];

  return (
    <div className="bm-wrapper">
      <div className="bm-container">

        {/* ===== PAGE HEADER ===== */}
        <div className="bm-page-header">
          <div>
            <h1 className="bm-page-title">Blog Management</h1>
            <p className="bm-page-subtitle">Create and manage blog posts for the platform</p>
          </div>
          <button className="bm-btn-publish" onClick={() => { resetForm(); setActiveTab('create'); }}>
            + New Blog Post
          </button>
        </div>

        {/* ===== MAIN TABS ===== */}
        <div className="bm-main-tabs">
          <button className={`bm-main-tab ${activeTab === 'list' ? 'bm-main-tab-active' : ''}`} onClick={() => setActiveTab('list')}>
            📋 All Blogs ({blogs.length})
          </button>
          <button className={`bm-main-tab ${activeTab === 'create' ? 'bm-main-tab-active' : ''}`} onClick={() => { resetForm(); setActiveTab('create'); }}>
            ✍️ Create New
          </button>
        </div>

        {/* ==================== LIST TAB ==================== */}
        {activeTab === 'list' && (
          <div className="bm-list-view">

            {/* Stats Cards */}
            <div className="bm-stats-row">
              <div className="bm-stat-card bm-stat-navy">
                <div className="bm-stat-icon">📄</div>
                <div className="bm-stat-val">{blogs.length}</div>
                <div className="bm-stat-lbl">Total Blogs</div>
              </div>
              <div className="bm-stat-card bm-stat-green">
                <div className="bm-stat-icon">✅</div>
                <div className="bm-stat-val">{publishedCount}</div>
                <div className="bm-stat-lbl">Published</div>
              </div>
              <div className="bm-stat-card bm-stat-orange">
                <div className="bm-stat-icon">📝</div>
                <div className="bm-stat-val">{draftCount}</div>
                <div className="bm-stat-lbl">Drafts</div>
              </div>
              <div className="bm-stat-card bm-stat-blue">
                <div className="bm-stat-icon">👁️</div>
                <div className="bm-stat-val">{totalViews.toLocaleString()}</div>
                <div className="bm-stat-lbl">Total Views</div>
              </div>
            </div>

            {/* Search & Filter */}
            <div className="bm-table-controls">
              <input
                type="text" className="bm-search"
                placeholder="🔍  Search blogs or authors..."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              />
              <div className="bm-filter-group">
                {['all', 'published', 'draft'].map(s => (
                  <button
                    key={s}
                    className={`bm-filter-btn ${filterStatus === s ? 'bm-filter-active' : ''}`}
                    onClick={() => setFilterStatus(s)}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="bm-table-wrap">
              <table className="bm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Blog Title</th>
                    <th>Category</th>
                    <th>Author</th>
                    <th>Date</th>
                    <th>Views</th>
                    <th>Comments</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="bm-empty">Loading blogs...</td>
                    </tr>
                  ) : filteredBlogs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="bm-empty">No blogs found</td>
                    </tr>
                  ) : filteredBlogs.map((blog, idx) => (
                    <tr key={blog.id}>
                      <td className="bm-td-num">{idx + 1}</td>
                      <td className="bm-td-title">{blog.title}</td>
                      <td><span className="bm-category-badge">{blog.category}</span></td>
                      <td className="bm-td-author">{blog.author}</td>
                      <td className="bm-td-date">{(blog.published_at || blog.created_at || '').toString().split('T')[0]}</td>
                      <td className="bm-td-center">{(blog.views || 0).toLocaleString()}</td>
                      <td className="bm-td-center">{blog.comments_count || 0}</td>
                      <td>
                        <span
                          className={`bm-status-badge ${blog.status === 'published' ? 'bm-badge-published' : 'bm-badge-draft'}`}
                          onClick={() => handleStatusToggle(blog.id)}
                          title="Click to toggle status"
                        >
                          {blog.status === 'published' ? '✅ Published' : '📝 Draft'}
                        </span>
                      </td>
                      <td>
                        <div className="bm-action-btns">
                          <button className="bm-act-edit" title="Edit" onClick={() => handleEdit(blog)}>✏️</button>
                          <button className="bm-act-delete" title="Delete" onClick={() => handleDelete(blog.id)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bm-table-footer">
              Showing {filteredBlogs.length} of {blogs.length} blogs
            </div>
          </div>
        )}

        {/* ==================== CREATE / EDIT TAB ==================== */}
        {activeTab === 'create' && (
          <div className="bm-create-view">

            {/* Top Actions */}
            <div className="bm-create-header">
              <button className="bm-btn-back" onClick={() => setActiveTab('list')}>← Back to List</button>
              <div className="bm-create-actions">
                <button className="bm-btn-draft" disabled={submitting} onClick={() => handleSubmit('draft')}>💾 Save Draft</button>
                <button className="bm-btn-publish" disabled={submitting} onClick={() => handleSubmit('published')}>🚀 {isEditing ? 'Update' : 'Publish'}</button>
              </div>
            </div>

            {/* Section Nav */}
            <div className="bm-section-nav">
              {sections.map(s => (
                <button
                  key={s.id}
                  className={`bm-section-btn ${activeSection === s.id ? 'bm-section-active' : ''}`}
                  onClick={() => setActiveSection(s.id)}
                >
                  {s.icon} {s.label}
                </button>
              ))}
            </div>

            {/* ===== BASIC INFO ===== */}
            {activeSection === 'basic' && (
              <div className="bm-section">
                <h2 className="bm-section-title">📝 Basic Information</h2>

                <div className="bm-field">
                  <label>Blog Title <span className="bm-req">*</span></label>
                  <input type="text" name="title" value={formData.title} onChange={handleTitleChange} placeholder="Enter an engaging blog title..." className={errors.title ? 'bm-error-input' : ''} />
                  {errors.title && <span className="bm-err-msg">{errors.title}</span>}
                  <span className="bm-hint">{formData.title.length}/100 characters</span>
                </div>

                <div className="bm-field">
                  <label>Blog Handle / Slug</label>
                  <div className="bm-handle-wrap">
                    <span className="bm-handle-prefix">blog/</span>
                    <input type="text" name="handle" value={formData.handle} onChange={handleChange} placeholder="auto-generated-from-title" />
                  </div>
                  <span className="bm-hint">Auto-generated from title. You can edit manually.</span>
                </div>

                <div className="bm-row">
                  <div className="bm-field">
                    <label>Category <span className="bm-req">*</span></label>
                    <select name="category" value={formData.category} onChange={handleChange} className={errors.category ? 'bm-error-input' : ''}>
                      <option value="">Select category...</option>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.category && <span className="bm-err-msg">{errors.category}</span>}
                  </div>
                  <div className="bm-field">
                    <label>Author Name <span className="bm-req">*</span></label>
                    <input type="text" name="author" value={formData.author} onChange={handleChange} placeholder="e.g. Dr. Ayesha Khan" className={errors.author ? 'bm-error-input' : ''} />
                    {errors.author && <span className="bm-err-msg">{errors.author}</span>}
                  </div>
                </div>

                <div className="bm-field" style={{ maxWidth: '200px' }}>
                  <label>Read Time (minutes)</label>
                  <input type="number" name="readTime" value={formData.readTime} onChange={handleChange} placeholder="e.g. 5" min="1" max="60" />
                </div>

                <div className="bm-field">
                  <label>Short Description / Excerpt <span className="bm-req">*</span></label>
                  <textarea name="excerpt" value={formData.excerpt} onChange={handleChange} rows="3" placeholder="Write a short summary..." className={errors.excerpt ? 'bm-error-input' : ''} />
                  {errors.excerpt && <span className="bm-err-msg">{errors.excerpt}</span>}
                  <span className="bm-hint">{formData.excerpt.length}/200 characters recommended</span>
                </div>

                <div className="bm-field">
                  <label>Tags</label>
                  <div className="bm-tag-input-wrap">
                    {tags.map(tag => (
                      <span key={tag} className="bm-tag">#{tag} <button onClick={() => removeTag(tag)}>×</button></span>
                    ))}
                    <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={handleTagKeyDown} placeholder={tags.length >= 10 ? 'Max 10 tags' : 'Type and press Enter...'} disabled={tags.length >= 10} />
                  </div>
                  <div className="bm-tag-suggestions">
                    {tagSuggestions.filter(t => !tags.includes(t)).slice(0, 6).map(t => (
                      <button key={t} className="bm-tag-suggest" onClick={() => addTag(t)}>+ {t}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ===== CONTENT ===== */}
            {activeSection === 'content' && (
              <div className="bm-section">
                <h2 className="bm-section-title">✍️ Blog Content</h2>
                <div className="bm-field">
                  <label>Full Blog Content <span className="bm-req">*</span></label>
                  <div className="bm-editor-toolbar">
                    {['H1','H2','B','I','U','🔗','• List','1. List','❝ Quote'].map(tool => (
                      <button key={tool} className="bm-tool-btn" type="button">{tool}</button>
                    ))}
                  </div>
                  <textarea name="content" value={formData.content} onChange={handleChange} rows="20" placeholder="Write your full blog content here..." className={`bm-content-area ${errors.content ? 'bm-error-input' : ''}`} />
                  {errors.content && <span className="bm-err-msg">{errors.content}</span>}
                  <span className="bm-hint">{formData.content.split(/\s+/).filter(Boolean).length} words</span>
                </div>
              </div>
            )}

            {/* ===== MEDIA ===== */}
            {activeSection === 'media' && (
              <div className="bm-section">
                <h2 className="bm-section-title">🖼️ Media</h2>
                <div className="bm-field">
                  <label>Thumbnail Image</label>
                  {thumbnailPreview || existingThumbnailUrl ? (
                    <div className="bm-thumb-preview">
                      <img src={thumbnailPreview || existingThumbnailUrl} alt="preview" />
                      <button className="bm-thumb-remove" onClick={() => { setThumbnail(null); setThumbnailPreview(null); setExistingThumbnailUrl(null); }}>🗑️ Remove</button>
                    </div>
                  ) : (
                    <label className="bm-upload-area">
                      <input type="file" accept="image/*" onChange={handleThumbnail} hidden />
                      <div className="bm-upload-icon">📸</div>
                      <div className="bm-upload-text">Click to upload thumbnail</div>
                      <div className="bm-upload-hint">PNG, JPG, WEBP — Recommended: 1200×630px</div>
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* ===== SEO ===== */}
            {activeSection === 'seo' && (
              <div className="bm-section">
                <h2 className="bm-section-title">🔍 SEO Settings</h2>
                <div className="bm-seo-preview">
                  <div className="bm-seo-preview-label">Google Preview</div>
                  <div className="bm-seo-url">smartspeechtherapy.com/blog/{formData.handle || 'your-blog-handle'}</div>
                  <div className="bm-seo-ptitle">{formData.metaTitle || formData.title || 'Your Blog Title'}</div>
                  <div className="bm-seo-pdesc">{formData.metaDescription || formData.excerpt || 'Your blog description...'}</div>
                </div>
                <div className="bm-field">
                  <label>Meta Title</label>
                  <input type="text" name="metaTitle" value={formData.metaTitle} onChange={handleChange} placeholder="SEO title (leave blank to use blog title)" />
                  <span className={`bm-hint ${formData.metaTitle.length > 60 ? 'bm-warn' : ''}`}>{formData.metaTitle.length}/60</span>
                </div>
                <div className="bm-field">
                  <label>Meta Description</label>
                  <textarea name="metaDescription" value={formData.metaDescription} onChange={handleChange} rows="3" placeholder="SEO description (leave blank to use excerpt)" />
                  <span className={`bm-hint ${formData.metaDescription.length > 160 ? 'bm-warn' : ''}`}>{formData.metaDescription.length}/160</span>
                </div>
              </div>
            )}

            {/* ===== SETTINGS ===== */}
            {activeSection === 'settings' && (
              <div className="bm-section">
                <h2 className="bm-section-title">⚙️ Post Settings</h2>
                <div className="bm-field">
                  <label>Publication Status</label>
                  <div className="bm-status-group">
                    {['draft', 'published', 'scheduled'].map(s => (
                      <label key={s} className={`bm-status-option ${formData.status === s ? 'bm-status-active' : ''}`}>
                        <input type="radio" name="status" value={s} checked={formData.status === s} onChange={handleChange} hidden />
                        <span className="bm-status-dot"></span>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="bm-toggles">
                  {[
                    { name: 'featured', label: 'Featured Post', desc: 'Show this post in featured section on homepage' },
                    { name: 'allowComments', label: 'Allow Comments', desc: 'Let users comment on this blog post' },
                  ].map(item => (
                    <div key={item.name} className="bm-toggle-item">
                      <div>
                        <div className="bm-toggle-label">{item.label}</div>
                        <div className="bm-toggle-desc">{item.desc}</div>
                      </div>
                      <label className="bm-toggle">
                        <input type="checkbox" name={item.name} checked={formData[item.name]} onChange={handleChange} />
                        <span className="bm-toggle-slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="bm-footer-actions">
              <button className="bm-btn-draft" disabled={submitting} onClick={() => handleSubmit('draft')}>💾 Save as Draft</button>
              <button className="bm-btn-publish" disabled={submitting} onClick={() => handleSubmit('published')}>🚀 {isEditing ? 'Update Blog' : 'Publish Blog'}</button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManagement;